/**
 * Log-time dialog. SPEC §7.4 has Harvest as the source of truth, but
 * the manual path lives here so staff aren't blocked when they can't
 * context-switch to Harvest. Beacon-origin entries land directly in the
 * `time_entry` table with a `beacon:<uuid>` sentinel so they don't
 * collide with Harvest IDs (a follow-up sync worker pushes them up to
 * Harvest later).
 */

import { Clock, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Textarea } from '@/components/ui/textarea.js';
import { useCreateTicketTimeEntry } from '@/hooks/use-tickets.js';
import { cn } from '@/lib/cn.js';

interface Props {
  open: boolean;
  onClose: () => void;
  ticketNumber: string;
  ticketSubject: string;
}

const HOURS_PRESETS = ['0.25', '0.5', '0.75', '1.0', '2.0'] as const;

function todayIsoDate(): string {
  // YYYY-MM-DD in local timezone — matches Harvest's spent_date convention.
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function LogTimeDialog({
  open,
  onClose,
  ticketNumber,
  ticketSubject,
}: Props) {
  const create = useCreateTicketTimeEntry(ticketNumber);

  const [hours, setHours] = useState('0.5');
  const [spentDate, setSpentDate] = useState(todayIsoDate());
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setHours('0.5');
    setSpentDate(todayIsoDate());
    setNotes('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !create.isPending) onClose();
      // ⌘↵ to submit — INTERACTION_SPEC keyboard contract
      if (
        (e.metaKey || e.ctrlKey) &&
        e.key === 'Enter' &&
        !create.isPending
      ) {
        e.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, create.isPending, hours, spentDate, notes]);

  if (!open) return null;

  function handleSubmit() {
    const h = parseFloat(hours);
    if (!Number.isFinite(h) || h <= 0)
      return toast.error('Hours must be greater than zero');
    if (h > 24) return toast.error('Hours must be 24 or less');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(spentDate))
      return toast.error('Date must be YYYY-MM-DD');

    create.mutate(
      {
        hours: h,
        spentDate,
        notes: notes.trim(),
      },
      {
        onSuccess: () => {
          toast.success(`Logged ${h}h on ${ticketNumber}`);
          onClose();
        },
      }
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dialog-in-backdrop"
      onClick={create.isPending ? undefined : onClose}
    >
      <div
        className="w-full max-w-lg mx-4 rounded-lg border border-ink-600 bg-ink-850 shadow-xl dialog-in-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="log-time-title"
      >
        <header className="flex items-start gap-3 px-5 pt-4 pb-3 border-b border-ink-700">
          <span className="w-9 h-9 rounded-md bg-beacon-400/15 text-beacon-400 border border-beacon-400/30 inline-flex items-center justify-center shrink-0">
            <Clock className="h-4 w-4" />
          </span>
          <div className="flex-1 min-w-0">
            <h2
              id="log-time-title"
              className="text-[15px] font-semibold text-ink-100"
            >
              Log time
            </h2>
            <p className="text-[12.5px] text-ink-300 mt-0.5 truncate">
              Against{' '}
              <span className="mono text-ink-100">{ticketNumber}</span> ·{' '}
              {ticketSubject}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={create.isPending}
            className="text-ink-300 hover:text-ink-100 focus-ring rounded-md p-1 disabled:opacity-50"
            aria-label="Close dialog"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-5 gap-3">
            <div className="col-span-2">
              <Field label="Hours *">
                <div className="relative">
                  <Input
                    value={hours}
                    disabled={create.isPending}
                    onChange={(e) => setHours(e.target.value)}
                    className="!pr-7 mono !text-[14px] font-semibold"
                    data-testid="ltd-hours"
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[12px] text-ink-400 mono pointer-events-none">
                    h
                  </span>
                </div>
              </Field>
              <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                {HOURS_PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setHours(p)}
                    disabled={create.isPending}
                    className={cn(
                      'h-6 px-2 mono text-[11px] rounded border focus-ring',
                      hours === p
                        ? 'bg-beacon-400/15 text-beacon-300 border-beacon-400/40'
                        : 'border-ink-600 text-ink-300 hover:text-ink-100'
                    )}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div className="col-span-3">
              <Field label="Date *">
                <Input
                  type="date"
                  value={spentDate}
                  disabled={create.isPending}
                  onChange={(e) => setSpentDate(e.target.value)}
                  data-testid="ltd-date"
                />
              </Field>
            </div>
          </div>

          <Field label="Notes">
            <Textarea
              rows={3}
              value={notes}
              disabled={create.isPending}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What did you do? Client-visible in the monthly report."
              data-testid="ltd-notes"
            />
          </Field>
        </div>

        <footer className="px-5 py-3 border-t border-ink-700 flex items-center gap-2">
          <span className="text-[11.5px] text-ink-400 mono">⌘↵ save</span>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={create.isPending}
              data-testid="ltd-submit"
            >
              {create.isPending ? 'Saving…' : 'Save entry'}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.06em] text-ink-300 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
